<template>
  <div class="settings-section">
    <h2 class="section-title">Print Settings</h2>
    <p class="section-description">Configure paper size and print preferences for invoices and receipts</p>

    <div class="settings-form">
      <div class="form-field">
        <label class="form-label">Paper Size</label>
        <div class="radio-group">
          <label class="radio-option" :class="{ active: paperSize === 'A4' }">
            <input
              type="radio"
              v-model="paperSize"
              value="A4"
              class="radio-input"
              @change="savePaperSize"
            />
            <span class="radio-label">
              <span class="radio-title">A4</span>
              <span class="radio-desc">210 x 297 mm — standard invoice size</span>
            </span>
          </label>
          <label class="radio-option" :class="{ active: paperSize === 'A5' }">
            <input
              type="radio"
              v-model="paperSize"
              value="A5"
              class="radio-input"
              @change="savePaperSize"
            />
            <span class="radio-label">
              <span class="radio-title">A5</span>
              <span class="radio-desc">148 x 210 mm — compact half-size</span>
            </span>
          </label>
          <label class="radio-option" :class="{ active: paperSize === '80mm' }">
            <input
              type="radio"
              v-model="paperSize"
              value="80mm"
              class="radio-input"
              @change="savePaperSize"
            />
            <span class="radio-label">
              <span class="radio-title">80mm Thermal</span>
              <span class="radio-desc">80mm width — standard POS thermal receipt</span>
            </span>
          </label>
          <label class="radio-option" :class="{ active: paperSize === '58mm' }">
            <input
              type="radio"
              v-model="paperSize"
              value="58mm"
              class="radio-input"
              @change="savePaperSize"
            />
            <span class="radio-label">
              <span class="radio-title">58mm Thermal</span>
              <span class="radio-desc">58mm width — compact thermal receipt</span>
            </span>
          </label>
        </div>
        <p class="field-hint">A4/A5 for full invoices, 80mm/58mm for thermal POS receipts</p>
      </div>

      <div v-if="saved" class="save-notice">
        Paper size saved.
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { usePrint } from '@shared/composables/usePrint.js'
import { useBilling } from '../../composables/useBilling.js'

const { paperSize, savePaperSize: persistLocal } = usePrint()
const { getBillingSettings, updateBillingSettings } = useBilling()
const saved = ref(false)

const savePaperSize = async () => {
  // Save to both localStorage (for instant reads) and API (server-persisted)
  persistLocal(paperSize.value)
  try {
    await updateBillingSettings({ paper_size: paperSize.value })
  } catch { /* API save failed, localStorage still has it */ }
  saved.value = true
  setTimeout(() => { saved.value = false }, 2000)
}

// Load paper size from API on mount (source of truth)
onMounted(async () => {
  try {
    const settings = await getBillingSettings()
    const data = settings?.data || settings
    if (data?.paper_size) {
      paperSize.value = data.paper_size
      persistLocal(data.paper_size)
    }
  } catch { /* use localStorage default */ }
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.settings-section {
  padding: var(--space-6);
}

.section-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-1) 0;
}

.section-description {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin: 0 0 var(--space-6) 0;
}

.settings-form {
  max-width: 500px;
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.radio-option {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.radio-option:hover {
  border-color: var(--color-blue);
  background: var(--color-blue-50, #eff6ff);
}

.radio-option.active {
  border-color: var(--color-blue);
  background: var(--color-blue-50, #eff6ff);
}

.radio-input {
  margin-top: 2px;
  accent-color: var(--color-blue);
  flex-shrink: 0;
}

.radio-label {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.radio-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
}

.radio-desc {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
}

.field-hint {
  font-size: var(--font-size-xs);
  color: var(--color-gray-400);
  margin: 0;
}

.save-notice {
  font-size: var(--font-size-sm);
  color: var(--color-green, #16a34a);
  padding: var(--space-2) var(--space-3);
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: var(--radius-md);
}
</style>
